import React, { useCallback, useMemo, type FC } from 'react'

import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  Button,
  Flex,
  FlexItem,
  FormElement,
  InputText,
  LINK_VARIANT_DEFAULT,
  Link,
  SidePanel,
  SidePanelHeader,
  SidePanelSeparator,
  Title,
  Toggle,
} from 'mailup-web-components'

import IconChevronsRight from 'Components/icons/IconChevronsRight'

import {
  panelEditChangeNameCallback,
  panelEditChangeNotesCallback,
  panelEditChangeTypeCallback,
  panelEditCloseCallback,
  panelEditSaveCallback,
} from 'Pages/GroupsList/recoil/callbacks/edit'
import {
  panelEditCanSaveState,
  panelEditState,
} from 'Pages/GroupsList/recoil/state/state'

const StyledFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: #fff;
`

const PanelEdit: FC = () => {
  const intl = useIntl()

  const { show, item, nameIsDirty, nameConflict } =
    useRecoilValue(panelEditState)

  const canSave = useRecoilValue(panelEditCanSaveState)

  const handleClose = useRecoilCallback(panelEditCloseCallback, [])
  const changeNotes = useRecoilCallback(panelEditChangeNotesCallback, [])
  const changeName = useRecoilCallback(panelEditChangeNameCallback, [])
  const changeType = useRecoilCallback(panelEditChangeTypeCallback, [])
  const handleSave = useRecoilCallback(panelEditSaveCallback, [])

  const handleNotesChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
      void changeNotes(value)
    },
    [changeNotes],
  )

  const handleNameChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
      void changeName(value)
    },
    [changeName],
  )

  const nameFeedback = useMemo(() => {
    return item !== undefined && item.Name.length === 0 && nameIsDirty
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : nameConflict
        ? intl.formatMessage({ id: 'Load.Common.GroupExists' })
        : undefined
  }, [intl, item, nameIsDirty, nameConflict])

  return (
    <SidePanel show={show} width="740px">
      <SidePanelHeader padding="16px 32px" showBorder>
        <Link
          variant={LINK_VARIANT_DEFAULT}
          onClick={handleClose}
          data-mu-test="close-side-panel"
          data-tip={intl.formatMessage({
            id: 'Load.RecipientsList.PanelCloseTooltip',
          })}
        >
          <IconChevronsRight width="14px" height="14px" />
        </Link>
      </SidePanelHeader>

      {item !== undefined && (
        <Flex
          flexDirection="column"
          gap="24px"
          padding="24px 32px"
          height="calc(100% - 123px)"
        >
          <Title>
            {intl.formatMessage({
              id:
                item.Id === 0
                  ? 'Load.GroupsList.PanelAddGroupTitle'
                  : 'Load.GroupsList.PanelEditGroupTitle',
            })}
          </Title>

          <FormElement
            label={intl.formatMessage({ id: 'Load.NewGroup.GroupName' })}
            feedback={nameFeedback}
            required
          >
            <InputText
              name="Name"
              value={item.Name}
              maxLength="50"
              onChange={handleNameChange}
            />
          </FormElement>

          <FormElement
            label={intl.formatMessage({ id: 'Load.NewGroup.AdminNotes' })}
          >
            <InputText
              name="Notes"
              value={item.Notes}
              maxLength="100"
              onChange={handleNotesChange}
            />
          </FormElement>

          <Flex alignItems="center" gap="8px">
            <label htmlFor="Test">
              {intl.formatMessage({ id: 'Load.NewGroup.CreateTestGroup' })}
            </label>
            <Toggle
              id="Test"
              checked={item.Type === 'Test'}
              onChange={changeType}
            />
          </Flex>

          <FlexItem flexGrow="1" />
        </Flex>
      )}

      <StyledFooter>
        <SidePanelSeparator />
        <Flex justifyContent="end" padding="16px 32px">
          <Button
            kind={BUTTON_VARIANT_PRIMARY}
            data-mu-test="edit-contacts__save"
            disabled={!canSave}
            onClick={handleSave}
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Save' })}
          </Button>
        </Flex>
      </StyledFooter>
    </SidePanel>
  )
}

export default PanelEdit
